import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { execApi } from '../../../services/api';
import './styles.css';
import { thisMonth } from '../../../utils/date';

export type MeetingsPeriod = 'mtd' | 'month-last' | 'quarter-last' | 'ytd';

// Month options for the backward-window picker — last 18 months, newest first.
const MONTH_OPTIONS: string[] = (() => {
  const out: string[] = [];
  const n = new Date();
  for (let i = 0; i < 18; i++) {
    const d = new Date(n.getFullYear(), n.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
})();


export type MeetingType = 'all' | 'psy' | 'cm' | 'psyIntake' | 'psyFu' | 'cmIntake' | 'cmFu';


type TypeBuckets = { psyIntake: number; psyFu: number; cmIntake: number; cmFu: number };
type Bucket = keyof TypeBuckets;
const fmtN = (n: number) => n.toLocaleString();

// Top meeting-type filter = MULTI-SELECT over the 4 specific buckets (no groups). Ohad 2026-07-07.
const TYPE_BUCKET_OPTIONS: Bucket[] = ['psyIntake', 'psyFu', 'cmIntake', 'cmFu'];
const TYPE_BUCKET_LABELS: Record<string, string> = {
  psyIntake: 'אינטייק פסיכ׳', psyFu: 'מעקב פסיכ׳', cmIntake: 'אינטייק CM', cmFu: 'מעקב CM',
};
type NoShowDim = { label: string; pct: number; count: number | null; total?: number | null; key?: string | null };

// Drill payload — mirrors backend exec_meetings.py MeetingsDrillView. All fields
// are real (meetings_raw + cross-refs on XanoView.Meeting). No mock.
interface DrillData {
  period: string;
  label: string;
  rangeStart: string;
  rangeEnd: string;
  volume: { total: number } & TypeBuckets;
  coordinated: { total: number } & TypeBuckets;   // "מתואמות" — whole-month Yoman (done+noshow+active)
  doneVolume: { total: number } & TypeBuckets;
  plannedToDate: { total: number } & TypeBuckets;
  status: { done: number; noShow: number; cancelled: number; recovered: number | null };
  cancel: {
    byType: TypeBuckets;
    byLeadTime: { label: string; count: number }[];
    recoveredPct: number | null;
    recoveredCount: number | null;
    recoveredByType?: TypeBuckets | null;
    recoveredTotalByType?: TypeBuckets | null;
    reasons: { label: string; count: number }[];
  };
  noShowByBucket: TypeBuckets;
  noShowByType: NoShowDim[];
  noShowByDay: NoShowDim[];
  noShowByHour: NoShowDim[];
  noShowByTherapist: { name: string; type: 'psy' | 'cm'; pct: number; count: number; total?: number }[];
  upcoming: { total: number; reIntake: number } & TypeBuckets;
  restOfToday: { total: number; reIntake: number } & TypeBuckets;
  whoBooks: {
    allStaffPct: number; allClientPct: number; fuStaffPct: number; fuClientPct: number; unknownPct: number;
    byType?: Record<string, { staffPct: number; clientPct: number; total: number }>;
  };
  monthlyTrend: { month: string; total: number; psy: number; cm: number; noShowPct: number }[];
}

// One filter, multi-select months (checkboxes). Used for both backward + forward.
function MonthMultiSelect({ label, options, selected, onChange, unit = 'חודשים', labels }: {
  label: string; options: string[]; selected: string[]; onChange: (v: string[]) => void;
  unit?: string; labels?: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const toggle = (m: string) => {
    const next = selected.includes(m) ? selected.filter((x) => x !== m) : [...selected, m];
    if (next.length) onChange(next);   // keep at least one selected
  };
  const summary = selected.length === 1 ? (labels?.[selected[0]] ?? selected[0]) : `${selected.length} ${unit}`;
  return (
    <div className="mtg-filter mtg-ms" ref={ref}>
      <label className="mtg-filter-label">{label}</label>
      <button
        type="button"
        className={`mtg-filter-select ${selected.length ? 'has-value' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >{summary} ▾</button>
      {open && (
        <div className="mtg-ms-panel">
          {options.map((m) => (
            <label key={m} className="mtg-ms-opt">
              <input type="checkbox" checked={selected.includes(m)} onChange={() => toggle(m)} />
              <span>{labels?.[m] ?? m}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const CLINIC_OPTIONS = ['Maccabi', 'Clalit', 'Maccabi Youth', 'ITC', 'Private', 'Donation'];
const CLINIC_LABELS: Record<string, string> = {
  Maccabi: 'מכבי', Clalit: 'כללית', 'Maccabi Youth': 'מכבי נוער',
  ITC: 'חוסן', Private: 'פרטי', Donation: 'פילנתרופיה',
};

export default function ExecMeetings() {
  const navigate = useNavigate();
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  // Backward window = a multi-select SET of months (default = current month).
  const [backMonths, setBackMonths] = useState<string[]>([thisMonth]);
  // Global cross-filter by meeting type — set from the top selector OR by
  // clicking any by-type bar in Section 1/2/3 (the patient-dashboard pattern).
  const [typeSel, setTypeSel] = useState<Bucket[]>(['psyIntake', 'psyFu', 'cmIntake', 'cmFu']);
  // Clinic (= קופה via clinic_group). Default = all selected (no-op filter).
  const [clinics, setClinics] = useState<string[]>(CLINIC_OPTIONS);

  const sortedBack = [...backMonths].sort();
  const periodLabel = sortedBack.length === 1
    ? sortedBack[0]
    : `${sortedBack[0]} — ${sortedBack[sortedBack.length - 1]}`;
  const isDefaultRange = backMonths.length === 1 && backMonths[0] === thisMonth;
  const clinicsAll = clinics.length === CLINIC_OPTIONS.length;

  return (
    <div className="mtg-wrap">
      <div className="mtg-header">
        <div className="mtg-title-block">
          <button className="mtg-back-btn" onClick={() => navigate('/exec')}>← חזרה</button>
          <h1 className="mtg-title">פגישות · ביטולים · No Show</h1>
        </div>
        <span className="mtg-chip">{periodLabel}</span>
      </div>

      {/* Filters + note — one unified bar, connected to the dashboard */}
      <div className="mtg-filterbar">
        <div className="mtg-filters">
          <MonthMultiSelect label="בחר תקופה" options={MONTH_OPTIONS} selected={backMonths} onChange={setBackMonths} />
          <MonthMultiSelect label="קופה" options={CLINIC_OPTIONS} selected={clinics} onChange={setClinics} unit="קופות" labels={CLINIC_LABELS} />
          <MonthMultiSelect label="סוג פגישה" options={TYPE_BUCKET_OPTIONS} selected={typeSel}
            onChange={(v) => setTypeSel(v as Bucket[])} unit="סוגים" labels={TYPE_BUCKET_LABELS} />
          {(!isDefaultRange || typeSel.length !== 4 || !clinicsAll) && (
            <button
              className="mtg-filter-clear"
              onClick={() => { setBackMonths([thisMonth]); setTypeSel([...TYPE_BUCKET_OPTIONS]); setClinics(CLINIC_OPTIONS); }}
            >נקה הכל</button>
          )}
        </div>
      </div>

      {/* Unified container — all sections read real data from meetings_raw via the
          API. The top type multi-select filters only the TOP numbers (hero + role
          cards + who-books); no-show / cancellations keep their own local filters. */}
      <MeetingsUnified backMonths={backMonths} clinics={clinics} typeSel={typeSel} />

      {/* Legacy Panel 1, 2, 4 removed — incorporated into MeetingsUnified
          (Section 2 = no-show deep-dive; Section 4 = 12-month chooser). */}

    </div>
  );
}

// ─── Unified container — Volume + Statuses + Cancel deep-dive ───────────────

function MeetingsUnified({
  backMonths, clinics, typeSel,
}: {
  backMonths: string[]; clinics: string[]; typeSel: Bucket[];
}) {
  const navigate = useNavigate();
  // No HMO scaling — numbers are real, as-is. (scale kept as 1 so the existing
  // s()/s1() helpers below stay unchanged.)
  const scale = 1;
  // Real drill data from meetings_raw via the API (volume + status + cancellation
  // + no-show breakdowns + upcoming + who-books + recovered). No mock fallback —
  // while loading / on error we render an explicit state, never fake numbers.
  const [apiDrill, setApiDrill] = useState<DrillData | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let live = true;
    setFailed(false);
    setLoading(true);
    // Forward view now FOLLOWS the selected period (Ohad 2026-07-01): pass backMonths
    // as the forward window too, so "מתוכנן קדימה" = active/future meetings WITHIN the
    // selected period (today → end of period), not a separate decoupled horizon.
    execApi.getMeetings(backMonths, backMonths, 'all', clinics)
      .then((res) => { if (live) setApiDrill(res.data); })
      .catch(() => { if (live) setFailed(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [backMonths, clinics]);

  if (!apiDrill) {
    return (
      <div className="mtg-unified">
        <div className="mtg-unified-section" style={{ textAlign: 'center', color: 'var(--ex-text-muted)' }}>
          {failed ? 'שגיאה בטעינת נתוני הפגישות' : 'טוען נתונים…'}
        </div>
      </div>
    );
  }
  const drill: DrillData = apiDrill;

  const s  = (n: number) => Math.round(n * scale);       // Sections 2/3/4

  // Top type MULTI-SELECT (Ohad 2026-07-07): the hero + role cards always render in
  // full; a bucket NOT in typeSel simply contributes 0. `g` zeroes an unselected
  // bucket's value; `selSum` sums a per-type object over the selected buckets only.
  const has = (b: Bucket) => typeSel.includes(b);
  const g = (b: Bucket, v: number) => (has(b) ? v : 0);
  const selSum = (o: TypeBuckets) => typeSel.reduce((acc, k) => acc + o[k], 0);
  const total      = selSum(drill.volume);
  const coordinatedNow = selSum(drill.coordinated);
  const doneNow    = selSum(drill.doneVolume);
  // No-show RATE = נושאו ÷ (בוצעו + נושאו). Raw (unscaled) values — the ratio is scale-invariant.
  const nsPct = (dn: number, ns: number) => (dn + ns > 0 ? ((ns / (dn + ns)) * 100).toFixed(2) : '0.00');
  const psyDoneR = g('psyIntake', drill.doneVolume.psyIntake) + g('psyFu', drill.doneVolume.psyFu);
  const psyNsR   = g('psyIntake', drill.noShowByBucket.psyIntake) + g('psyFu', drill.noShowByBucket.psyFu);
  const cmDoneR  = g('cmIntake', drill.doneVolume.cmIntake) + g('cmFu', drill.doneVolume.cmFu);
  const cmNsR    = g('cmIntake', drill.noShowByBucket.cmIntake) + g('cmFu', drill.noShowByBucket.cmFu);
  const avgNsPct = nsPct(psyDoneR + cmDoneR, psyNsR + cmNsR);
  // psy/cm volume totals (sum of the two sub-types) — for the volume cards.

  // Month-over-month volume delta — only for a single, fully-PAST month (the current
  // month is partial, so a delta would read as a false drop). From monthlyTrend.
  const curMonthId = thisMonth();
  const momDelta = (() => {
    if (backMonths.length !== 1) return null;
    const sel = backMonths[0];
    if (sel >= curMonthId) return null;                      // skip current/future (partial)
    const idx = drill.monthlyTrend.findIndex((m) => m.month === sel);
    if (idx < 1) return null;
    const prev = drill.monthlyTrend[idx - 1].total;
    const cur = drill.monthlyTrend[idx].total;
    if (!prev) return null;
    return Math.round(((cur - prev) / prev) * 100);
  })();



  return (
    <div className="mtg-unified" style={{ position: 'relative' }}>
      {loading && <div className="mtg-updating">מסנן…</div>}
      {/* Section 1 — Numbers (volume + statuses) with LOCAL meeting-type filter */}
      <div className="mtg-unified-section">
        <div className="mtg-section-head">
          <div>
            <span className="mtg-section-title">מספרי הפגישות בתקופה</span>
          </div>
        </div>
        <div className="mtg-volume-row">
            <div className="mtg-vol-card hero">
              <div style={{ display: 'flex', gap: 40, justifyContent: 'center', alignItems: 'flex-start' }}>
                <div style={{ textAlign: 'center', cursor: 'help' }} title="כל הפגישות המתואמות ביומן לתקופה (בוצעו + No Show + עתידיות)">
                  <div className="mtg-vol-label">תואמו</div>
                  <div className="mtg-vol-big">{fmtN(coordinatedNow)}</div>
                </div>
                <div style={{ textAlign: 'center', cursor: 'help' }} title="כמה פגישות בוצעו בפועל בתקופה (סטטוס done) — לא כולל No Show">
                  <div className="mtg-vol-label">בוצעו</div>
                  <div className="mtg-vol-big" style={{ color: '#047857' }}>{fmtN(doneNow)}</div>
                </div>
                <div style={{ textAlign: 'center', cursor: 'help' }} title="אחוז ה-No Show הממוצע: פגישות בסטטוס noshow מתוך אלה שהתקיימו (done + noshow)">
                  <div className="mtg-vol-label">No Show (ממוצע)</div>
                  <div className="mtg-vol-big" style={{ color: '#EF4444' }}>{avgNsPct}%</div>
                </div>
              </div>
              {momDelta != null && (
                <div className="mtg-vol-sub" style={{ color: momDelta >= 0 ? '#047857' : '#EF4444', fontWeight: 700 }}>
                  {momDelta >= 0 ? '+' : ''}{momDelta}% מול חודש קודם (מספר הפגישות)
                </div>
              )}
              <WhoBooks total={total} who={drill.whoBooks} />
            </div>
            <div className="mtg-vol-card">
              <div className="mtg-vol-label role-title">פסיכיאטר · בוצעו</div>
              <div className="mtg-vol-body" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <div className="mtg-vol-big">{fmtN(s(g('psyIntake', drill.doneVolume.psyIntake)) + s(g('psyFu', drill.doneVolume.psyFu)))}</div>
                <NsRows rows={[
                  { label: 'אינטייק', done: s(g('psyIntake', drill.doneVolume.psyIntake)), ns: g('psyIntake', drill.noShowByBucket.psyIntake) },
                  { label: 'מעקב', done: s(g('psyFu', drill.doneVolume.psyFu)), ns: g('psyFu', drill.noShowByBucket.psyFu) },
                ]} />
              </div>
              <IntakeSplit intake={s(g('psyIntake', drill.doneVolume.psyIntake))} followup={s(g('psyFu', drill.doneVolume.psyFu))} tone="psy" />
            </div>
            <div className="mtg-vol-card">
              <div className="mtg-vol-label role-title">מנהל תיק · בוצעו</div>
              <div className="mtg-vol-body" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <div className="mtg-vol-big">{fmtN(s(g('cmIntake', drill.doneVolume.cmIntake)) + s(g('cmFu', drill.doneVolume.cmFu)))}</div>
                <NsRows rows={[
                  { label: 'אינטייק', done: s(g('cmIntake', drill.doneVolume.cmIntake)), ns: g('cmIntake', drill.noShowByBucket.cmIntake) },
                  { label: 'מעקב', done: s(g('cmFu', drill.doneVolume.cmFu)), ns: g('cmFu', drill.noShowByBucket.cmFu) },
                ]} />
              </div>
              <IntakeSplit intake={s(g('cmIntake', drill.doneVolume.cmIntake))} followup={s(g('cmFu', drill.doneVolume.cmFu))} tone="cm" />
            </div>
          </div>
        {/* Status row removed 2026-07-01 (Ohad): Done was redundant with the hero,
            cancellations dropped (cancellation_time only ~65% populated → uncertain),
            no-show moved INTO the role cards. Detail lives in the no-show deep-dive below. */}
      </div>

      {/* No-show deep-dive — its OWN local filter (click a bar → filters only this
          panel, via an independent fetch). By type / day / hour / therapist. */}
      <NoShowSection drill={drill} backMonths={backMonths} clinics={clinics} />

      {/* Cancellations deep-dive — its own local filter too. Reasons, lead-time,
          by type, refill %. */}
      <CancelSection drill={drill} backMonths={backMonths} clinics={clinics} />

      {/* Layer 3 link — the per-meeting explorer is a SEPARATE page now (control tower:
          it doesn't load with the drill). (Ohad 2026-07-01) */}
      <div className="mtg-unified-section" style={{ textAlign: 'center' }}>
        <button
          onClick={() => navigate('/exec/meetings/list')}
          style={{ background: 'var(--ex-blue-bar)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontWeight: 700 }}
        >
          סייר פגישות · רשימה מלאה פר-פגישה ←
        </button>
      </div>
    </div>
  );
}

// "מי קובע" — staff vs client split, shown next to the main total (Ohad 2026-06-15).
// Intake vs follow-up split bar for a role hero card (psy / cm), styled like WhoBooks.
// Per-role no-show table: one row per type (אינטייק / מעקב) → בוצעו · No Show count · No Show %.
// done = DONE only (no-show is NOT part of it); rate = ns / (done + ns).
function NsRows({ rows }: { rows: { label: string; done: number; ns: number }[] }) {
  const nspct = (d: number, n: number) => (d + n > 0 ? ((n / (d + n)) * 100).toFixed(2) : '0.00');
  const gt = '78px 80px 90px 74px';
  return (
    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ display: 'grid', gridTemplateColumns: gt, gap: 14, fontSize: 13, color: 'var(--ex-text-muted)' }}>
        <span /><span>בוצעו</span><span style={{ color: '#EF4444' }}>No Show</span><span style={{ color: '#EF4444' }}>%</span>
      </div>
      {rows.map((r) => (
        <div key={r.label} style={{ display: 'grid', gridTemplateColumns: gt, gap: 14, alignItems: 'baseline' }}>
          <span style={{ fontSize: 16, color: 'var(--ex-text-secondary)' }}>{r.label}</span>
          <span style={{ fontSize: 24, fontWeight: 800, color: '#047857' }}>{fmtN(r.done)}</span>
          <span style={{ fontSize: 24, fontWeight: 800, color: '#EF4444' }}>{fmtN(r.ns)}</span>
          <span style={{ fontSize: 18, fontWeight: 600, color: '#EF4444' }}>{nspct(r.done, r.ns)}%</span>
        </div>
      ))}
    </div>
  );
}

function IntakeSplit({ intake, followup, tone }: { intake: number; followup: number; tone: 'psy' | 'cm' }) {
  const total = intake + followup || 1;
  const intakePct = Math.round((intake / total) * 100);
  return (
    <div className={`mtg-split tone-${tone}`}>
      <div className="mtg-split-bar">
        <div className="mtg-split-seg intake" style={{ width: `${(intake / total) * 100}%` }} />
        <div className="mtg-split-seg fu" style={{ width: `${(followup / total) * 100}%` }} />
      </div>
      <div className="mtg-split-legend">
        <span><b>{intakePct}%</b> אינטייק · {intake.toLocaleString()}</span>
        <span><b>{100 - intakePct}%</b> מעקב · {followup.toLocaleString()}</span>
      </div>
    </div>
  );
}

function WhoBooks({ total, who, typeKey = 'all' }: { total: number; who: DrillData['whoBooks']; typeKey?: string }) {
  // Use the per-type split when filtered; fall back to the overall split.
  const tb = who.byType?.[typeKey];
  const clientPct = tb ? tb.clientPct : who.allClientPct;
  const staffPct = tb ? tb.staffPct : who.allStaffPct;
  const client = Math.round(total * clientPct);
  const staff = total - client;
  return (
    <div className="mtg-whobooks">
      <div className="mtg-whobooks-title">מי קבע</div>
      <div className="mtg-whobooks-bar">
        <div className="mtg-whobooks-seg client" style={{ width: `${clientPct * 100}%` }} />
        <div className="mtg-whobooks-seg staff" style={{ width: `${staffPct * 100}%` }} />
      </div>
      <div className="mtg-whobooks-legend">
        <span className="client"><b>{Math.round(clientPct * 100)}%</b> לקוח · {fmtN(client)}</span>
        <span className="staff"><b>{Math.round(staffPct * 100)}%</b> צוות · {fmtN(staff)}</span>
      </div>
      {typeKey === 'all' && (
        <div className="mtg-whobooks-note">
          במעקבים: {Math.round(who.fuClientPct * 100)}% לקוח (שירות עצמי אמיתי)
        </div>
      )}
    </div>
  );
}

// Fetch drill data scoped to a specific meeting-type, for a panel's OWN local filter —
// independent of the page filter, so clicking a bar in one panel doesn't move the other.
// Returns the fallback (page-level) drill while type='all' or during load.
function useDrillForType(backMonths: string[], clinics: string[] | undefined, type: MeetingType, fallback: DrillData): DrillData {
  const [data, setData] = useState<DrillData | null>(null);
  const key = `${backMonths.join(',')}|${(clinics ?? []).join(',')}|${type}`;
  useEffect(() => {
    if (type === 'all') { setData(null); return; }
    let live = true;
    execApi.getMeetings(backMonths, backMonths, type, clinics)
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return type === 'all' ? fallback : (data ?? fallback);
}

const BUCKET_LABEL: Record<string, string> = {
  psyIntake: 'אינטייק פסיכ׳', psyFu: 'מעקב פסיכ׳', cmIntake: 'אינטייק מנהל-תיק', cmFu: 'מעקב מנהל-תיק',
};

// No-show deep-dive — where/when/who no-shows concentrate. EVERY bar is clickable and
// sets one filter dimension (type / day / 2-hour slot / therapist). Cross-filter: each
// block is computed with the OTHER active dims applied but not its own, so you can always
// re-pick within a dimension. All real, from meetings_raw (via /noshow-breakdown).
// Multi-select cross-filter (Ohad 2026-07-07): each dimension is a SET of chosen keys.
// OR within a dimension, AND across dimensions. Empty array = no filter on that dimension.
type NSFilter = { types: string[]; dows: string[]; hours: string[]; thers: string[] };
const DOW_LABEL: Record<string, string> = { '1': 'ראשון', '2': 'שני', '3': 'שלישי', '4': 'רביעי', '5': 'חמישי', '6': 'שישי', '7': 'שבת' };
const DOW_KEY: Record<string, string> = { 'ראשון': '1', 'שני': '2', 'שלישי': '3', 'רביעי': '4', 'חמישי': '5', 'שישי': '6', 'שבת': '7' };
const hourLabel = (h: string) => `${String(h).padStart(2, '0')}-${String(Number(h) + 2).padStart(2, '0')}`;

type NSData = { byType: NoShowDim[]; byDay: NoShowDim[]; byHour: NoShowDim[]; byTherapist: DrillData['noShowByTherapist'] };

function useNoShowBreakdown(backMonths: string[], clinics: string[] | undefined, f: NSFilter, drill: DrillData): NSData {
  // Seed from the already-loaded drill so the panel paints instantly; the fetch (which
  // carries the raw filter keys) replaces it a beat later and on every filter change.
  const seed: NSData = {
    byType: [
      { label: 'אינטייק פסיכ׳', key: 'psyIntake', pct: 0, count: drill.noShowByBucket.psyIntake, total: null },
      { label: 'מעקב פסיכ׳', key: 'psyFu', pct: 0, count: drill.noShowByBucket.psyFu, total: null },
      { label: 'אינטייק מנהל-תיק', key: 'cmIntake', pct: 0, count: drill.noShowByBucket.cmIntake, total: null },
      { label: 'מעקב מנהל-תיק', key: 'cmFu', pct: 0, count: drill.noShowByBucket.cmFu, total: null },
    ],
    byDay: (drill.noShowByDay ?? []).map((d) => ({ ...d, key: DOW_KEY[d.label] ?? null })),
    byHour: (drill.noShowByHour ?? []).map((d) => ({ ...d, key: String(parseInt(d.label.slice(0, 2), 10)) })),
    byTherapist: drill.noShowByTherapist ?? [],
  };
  const [bd, setBd] = useState<NSData | null>(null);
  const key = `${backMonths.join(',')}|${(clinics ?? []).join(',')}|${f.types.join(',')}|${f.dows.join(',')}|${f.hours.join(',')}|${f.thers.join(',')}`;
  useEffect(() => {
    let live = true;
    execApi.getNoShowBreakdown({
      backMonths, clinics,
      types: f.types, dows: f.dows, hours: f.hours, therapists: f.thers,
    })
      .then((r) => { if (live) setBd({ byType: r.data.by_type, byDay: r.data.by_day, byHour: r.data.by_hour, byTherapist: r.data.by_therapist }); })
      .catch(() => { if (live) setBd(null); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return bd ?? seed;
}

const NOSHOW_HELP = [
  'המספר ואורך הבר = כמות פגישות ה-No Show בחתך.',
  'האחוז = שיעור ה-No Show בחתך: מתוך הפגישות שהתקיימו בחתך, כמה אחוז היו No Show.',
  'לדוגמה: 10% ביום ראשון = מתוך פגישות יום ראשון, 10% היו No Show.',
  '',
  'לחצו על בר לסינון הפאנל (הסינונים מצטברים).',
].join('\n');

function NoShowSection({ drill, backMonths, clinics }: { drill: DrillData; backMonths: string[]; clinics?: string[] }) {
  const [f, setF] = useState<NSFilter>({ types: [], dows: [], hours: [], thers: [] });
  const [therRoles, setTherRoles] = useState<('psy' | 'cm')[]>(['psy', 'cm']);   // multi: both by default
  const [therPage, setTherPage] = useState(0);   // therapist block pagination (5/page)
  const bd = useNoShowBreakdown(backMonths, clinics, f, drill);
  // pct = no-show RATE = noshow ÷ (done+noshow). LABEL = that rate; fraction in tooltip.
  // BAR = volume (share of the block's total no-shows) so length shows where they pile up.
  const pctOf = (v: number) => Math.round(v * 100);
  // Total no-show (count + rate) for the current filter — shown in the section title.
  const nsTotal = bd.byType.reduce((a, d) => a + (d.count ?? 0), 0);
  const nsSched = bd.byType.reduce((a, d) => a + (d.total ?? 0), 0);
  const nsRate = nsSched ? ((nsTotal / nsSched) * 100).toFixed(2) : '0.00';

  // One clickable row renderer for every dimension. `dim` = which filter SET it toggles.
  // Multi-select: clicking adds/removes the key from that dimension's array. count · % always shown.
  const dimRows = (
    dims: NoShowDim[], dim: keyof NSFilter,
    showCount = true,   // show "count · %" everywhere (Ohad 2026-07-07); false = "%" only
    barTotalOverride?: number,   // keep bar scale stable across paginated slices (therapists)
  ) => {
    const sel = f[dim];
    const hasFilter = sel.length > 0;
    const barTotal = (barTotalOverride ?? dims.reduce((a, d) => a + (d.count ?? 0), 0)) || 1;
    return dims.map((d) => {
      const noData = d.total != null && d.total === 0;
      const active = d.key != null && sel.includes(d.key);
      const toggle = () => {
        if (d.key == null) return;
        const k = d.key;
        setF((p) => ({ ...p, [dim]: p[dim].includes(k) ? p[dim].filter((x) => x !== k) : [...p[dim], k] }));
      };
      return (
        <div className="mtg-noshow-row clickable" key={d.key ?? d.label} role="button" tabIndex={0}
          onClick={toggle} style={{ opacity: hasFilter && !active ? 0.4 : 1 }}>
          <span style={{ minWidth: 64, fontSize: 13, color: active ? 'var(--ex-text-primary)' : 'var(--ex-text-secondary)', fontWeight: active ? 700 : 400 }}>{d.label}</span>
          {showCount && (
            <span style={{ fontSize: 12.5, color: 'var(--ex-text-muted)', textAlign: 'end' }}
              title="מספר ה-no-show בחתך (= אורך הבר)">
              {noData ? '' : (d.count ?? 0).toLocaleString()}
            </span>
          )}
          <div className="mtg-noshow-bar-wrap">
            {!noData && <div className="mtg-noshow-bar" style={{ width: `${((d.count ?? 0) / barTotal) * 100}%`, background: active ? 'var(--ex-blue-bar)' : undefined }} />}
          </div>
          <span className={`mtg-noshow-pct${!noData && pctOf(d.pct) >= 15 ? ' high' : ''}`}
            style={{ color: noData ? 'var(--ex-text-muted)' : undefined }}
            title={noData ? 'לא התקיימו פגישות בחתך זה' : (d.total != null ? `שיעור: ${d.count}/${d.total} נשאו` : undefined)}>
            {noData ? '—' : `${(d.pct * 100).toFixed(2)}%`}
            {!noData && d.total != null && (
              <span className="mtg-noshow-base">מ-{d.total.toLocaleString()}</span>
            )}
          </span>
        </div>
      );
    });
  };

  // Therapist block: tabbed by role (psy / cm), full scrollable list (no top-8 cap). Ohad 2026-07-07.
  const therDims: NoShowDim[] = [...(bd.byTherapist ?? [])]
    .filter((t) => therRoles.includes(t.type))
    .sort((a, b) => b.pct - a.pct)
    .map((t) => ({ label: t.name, key: t.name, pct: t.pct, count: t.count, total: t.total ?? null }));
  // Paginate 5/page (Ohad 2026-07-07) instead of a scroll that clips a row mid-height.
  const THER_PER = 5;
  const therPageCount = Math.max(1, Math.ceil(therDims.length / THER_PER));
  const therPg = Math.min(therPage, therPageCount - 1);
  const therTotalCount = therDims.reduce((a, d) => a + (d.count ?? 0), 0);
  const therPageDims = therDims.slice(therPg * THER_PER, therPg * THER_PER + THER_PER);

  const chips: { label: string; clear: () => void }[] = [
    ...f.types.map((t) => ({ label: BUCKET_LABEL[t] ?? t, clear: () => setF((p) => ({ ...p, types: p.types.filter((x) => x !== t) })) })),
    ...f.dows.map((d) => ({ label: DOW_LABEL[d] ?? d, clear: () => setF((p) => ({ ...p, dows: p.dows.filter((x) => x !== d) })) })),
    ...f.hours.map((h) => ({ label: hourLabel(h), clear: () => setF((p) => ({ ...p, hours: p.hours.filter((x) => x !== h) })) })),
    ...f.thers.map((t) => ({ label: t, clear: () => setF((p) => ({ ...p, thers: p.thers.filter((x) => x !== t) })) })),
  ];

  return (
    <div className="mtg-unified-section" id="mtg-sec-noshow">
      <div className="mtg-section-head">
        <div>
          <span className="mtg-section-title">
No Show
            <span title={NOSHOW_HELP} role="img" aria-label="הסבר"
              style={{ marginInlineStart: 6, cursor: 'help', color: 'var(--ex-text-muted)', fontSize: 13, fontWeight: 400 }}>ⓘ</span>
            <span style={{ marginInlineStart: 12, color: 'var(--ex-text-secondary)', fontWeight: 600 }}>· {nsTotal.toLocaleString()} מתוך {nsSched.toLocaleString()} · {nsRate}%</span>
          </span>
        </div>
        {chips.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {chips.map((c) => (
              <button key={c.label} onClick={c.clear}
                style={{ border: '1px solid var(--ex-blue-border)', borderRadius: 999, padding: '4px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 700, background: 'var(--ex-blue-bar)', color: '#fff' }}>
                {c.label} ✕
              </button>
            ))}
            {chips.length > 1 && (
              <button onClick={() => setF({ types: [], dows: [], hours: [], thers: [] })}
                style={{ border: '1px solid var(--ex-border)', borderRadius: 999, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: 'var(--ex-text-muted)', background: 'none' }}>
                נקה הכל
              </button>
            )}
          </div>
        )}
      </div>
      <div className="mtg-noshow-grid">
        <div className="mtg-noshow-block">
          <div className="mtg-noshow-block-title">לפי תפקיד וסוג</div>
          <div className="mtg-noshow-rows">{dimRows(bd.byType, 'types')}</div>
        </div>
        <div className="mtg-noshow-block">
          <div className="mtg-noshow-block-title">לפי יום בשבוע</div>
          <div className="mtg-noshow-rows">{dimRows(bd.byDay, 'dows')}</div>
        </div>
        <div className="mtg-noshow-block">
          <div className="mtg-noshow-block-title">לפי שעה ביום</div>
          <div className="mtg-noshow-rows">{dimRows(bd.byHour, 'hours')}</div>
        </div>
        <div className="mtg-noshow-block">
          <div className="mtg-noshow-block-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span>מטפלים עם no-show גבוה</span>
            <span style={{ display: 'inline-flex', gap: 4 }}>
              {(['psy', 'cm'] as const).map((t) => {
                const on = therRoles.includes(t);
                return (
                  <button key={t} onClick={() => { setTherPage(0); setTherRoles((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t]); }}
                    style={{ border: '1px solid var(--ex-border)', background: on ? 'var(--ex-blue-bar)' : 'transparent', color: on ? '#fff' : 'var(--ex-text-secondary)', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {t === 'psy' ? 'פסיכיאטרים' : 'מנהלי תיק'}
                  </button>
                );
              })}
            </span>
          </div>
          <div className="mtg-noshow-rows">{dimRows(therPageDims, 'thers', true, therTotalCount)}</div>
          {therPageCount > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginTop: 8, fontSize: 12, color: 'var(--ex-text-muted)' }}>
              <button type="button" disabled={therPg === 0} onClick={() => setTherPage(therPg - 1)}
                style={{ background: 'none', border: '1px solid var(--ex-border)', borderRadius: 6, color: 'inherit', padding: '1px 9px', cursor: therPg === 0 ? 'default' : 'pointer', opacity: therPg === 0 ? 0.4 : 1 }}>›</button>
              <span>{therPg * THER_PER + 1}–{Math.min((therPg + 1) * THER_PER, therDims.length)} מתוך {therDims.length}</span>
              <button type="button" disabled={therPg >= therPageCount - 1} onClick={() => setTherPage(therPg + 1)}
                style={{ background: 'none', border: '1px solid var(--ex-border)', borderRadius: 6, color: 'inherit', padding: '1px 9px', cursor: therPg >= therPageCount - 1 ? 'default' : 'pointer', opacity: therPg >= therPageCount - 1 ? 0.4 : 1 }}>‹</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Cancellations deep-dive — reasons / lead-time / by type / refill. All from meetings_raw.
// Note: canceled meetings are counted by MEETING date (not cancellation date); the
// cancellation-date snapshot is a separate view (cancellation_time only ~65% populated).
function CancelSection({ drill, backMonths, clinics }: { drill: DrillData; backMonths: string[]; clinics?: string[] }) {
  const [sel, setSel] = useState<MeetingType>('all');
  const d = useDrillForType(backMonths, clinics, sel, drill);
  const c = d.cancel;                    // lead-time + reasons follow the local filter
  const cAll = drill.cancel;             // full by-type (for the clickable bars)
  const totalCancel = cAll.byType.psyIntake + cAll.byType.psyFu + cAll.byType.cmIntake + cAll.byType.cmFu;
  // Bar = VOLUME (share of the block's total cancellations) so the full track = total,
  // fair to the eye. Label = that share %, aligned; the raw count lives in the tooltip.
  // `total` defaults to the sum of the items (use the un-sliced total for top-N lists).
  const bars = (items: { label: string; count: number }[], total?: number, labelW = 130) => {
    const tot = (total ?? items.reduce((a, i) => a + i.count, 0)) || 1;
    return items.map((i) => (
      <div className="mtg-noshow-row" key={i.label}
        style={{ display: 'grid', gridTemplateColumns: `${labelW}px 1fr 44px`, alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 13, color: 'var(--ex-text-secondary)', lineHeight: 1.2 }}>{i.label}</span>
        <div className="mtg-noshow-bar-wrap">
          <div className="mtg-noshow-bar" style={{ width: `${(i.count / tot) * 100}%`, background: 'var(--ex-amber, #F59E0B)' }} />
        </div>
        <span className="mtg-noshow-pct" style={{ textAlign: 'end' }} title={`${fmtN(i.count)} ביטולים`}>
          {tot > 0 ? Math.round((i.count / tot) * 100) : 0}%
        </span>
      </div>
    ));
  };
  // By-type bars: clickable to filter THIS panel; each shows count + share of total.
  // (Re-fill / "אוישו מחדש" is a SEPARATE question — recovery, not the cancellation
  //  itself — so it lives on its own, not mixed into "how many & why". Ohad 2026-07-05.)
  const typeItems: { key: MeetingType; b: keyof TypeBuckets; label: string }[] = [
    { key: 'psyIntake', b: 'psyIntake', label: 'אינטייק פסיכ׳' },
    { key: 'psyFu', b: 'psyFu', label: 'מעקב פסיכ׳' },
    { key: 'cmIntake', b: 'cmIntake', label: 'אינטייק מנהל-תיק' },
    { key: 'cmFu', b: 'cmFu', label: 'מעקב מנהל-תיק' },
  ];
  const barDenom = totalCancel || 1;   // volume scale: full track = total cancellations
  return (
    <div className="mtg-unified-section" id="mtg-sec-cancel">
      <div className="mtg-section-head">
        <div>
          <span className="mtg-section-title">ביטולים — כמה, מתי ולמה</span>
          <span className="mtg-section-sub" style={{ display: 'block', marginTop: 2 }}>
            {fmtN(totalCancel)} ביטולים בתקופה (נספרים לפי תאריך הפגישה) · האחוזים = חלקו של החתך מסך הביטולים · לחצו בר לסינון הפאנל
          </span>
        </div>
        {sel !== 'all' && (
          <button onClick={() => setSel('all')} style={{ border: '1px solid var(--ex-border)', borderRadius: 999, padding: '4px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 700, background: 'var(--ex-amber, #F59E0B)', color: '#fff' }}>
            מסונן: {BUCKET_LABEL[sel] ?? sel} · נקה ✕
          </button>
        )}
      </div>
      <div className="mtg-noshow-grid">
        <div className="mtg-noshow-block">
          <div className="mtg-noshow-block-title">לפי סוג</div>
          <div className="mtg-noshow-rows">
            {typeItems.map((i) => {
              const cnt = cAll.byType[i.b]; const active = sel === i.key;
              return (
                <div className="mtg-noshow-row clickable" key={i.label} role="button" tabIndex={0}
                  onClick={() => setSel(active ? 'all' : i.key)}
                  style={{ opacity: sel === 'all' || active ? 1 : 0.45, gridTemplateColumns: '96px 52px 1fr 52px' }}>
                  <span style={{ fontSize: 13, color: active ? 'var(--ex-text-primary)' : 'var(--ex-text-secondary)', fontWeight: active ? 700 : 400 }}>{i.label}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--ex-text-muted)', textAlign: 'end' }} title="מספר הביטולים">{fmtN(cnt)}</span>
                  <div className="mtg-noshow-bar-wrap" style={{ flex: 1 }}>
                    <div className="mtg-noshow-bar" style={{ width: `${(cnt / barDenom) * 100}%`, background: active ? 'var(--ex-blue-bar)' : 'var(--ex-amber, #F59E0B)' }} />
                  </div>
                  <span className="mtg-noshow-pct" style={{ textAlign: 'end' }} title="חלקו מסך הביטולים">
                    {barDenom > 0 ? Math.round((cnt / barDenom) * 100) : 0}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mtg-noshow-block">
          <div className="mtg-noshow-block-title">כמה זמן לפני הפגישה</div>
          <div className="mtg-noshow-rows">{bars(c.byLeadTime)}</div>
        </div>
        <div className="mtg-noshow-block" style={{ gridColumn: '1 / -1' }}>
          <div className="mtg-noshow-block-title">סיבות הביטול</div>
          <div className="mtg-noshow-rows">{bars(c.reasons.slice(0, 12), c.reasons.reduce((a, r) => a + r.count, 0), 210)}</div>
        </div>
      </div>
    </div>
  );
}

// Forward-looking — scheduled (active) meetings from now to the chosen horizon,
// by role × type. Has its own forward-only horizon selector.
